import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import RCSlider from 'rc-slider';
import Heading from '../heading';
import { VALUE_LABEL_DISPLAY_TYPES, SIZES } from './constants';

const { createSliderWithTooltip, Handle } = RCSlider;
const RCSliderWithTooltip = createSliderWithTooltip(RCSlider);

const defaultHandleProps = {};
const defaultValueFormatter = (value) => value;

const Slider = ({
  startPoint = 0,
  size = SIZES.medium,
  isTooltipEnabled = false,
  defaultValue = 0,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  onChange = Function.prototype,
  onFocus = Function.prototype,
  onBlur = Function.prototype,
  onBeforeChange = Function.prototype,
  onAfterChange = Function.prototype,
  value = null,
  tooltipValueFormatter = defaultValueFormatter,
  valueLabelDisplay = VALUE_LABEL_DISPLAY_TYPES.off,
  handleProps = defaultHandleProps,
  indeterminate = false,
}) => {
  const [isHandleFocus, setHandleFocus] = useState(false);

  const isValueLabelDisplayed = valueLabelDisplay === VALUE_LABEL_DISPLAY_TYPES.on;

  const currentClasses = classnames('ws-ui-slider', `ws-ui-slider--${size}`, {
    'ws-ui-slider--disabled': disabled,
    'ws-ui-slider--focused': isHandleFocus,
    'ws-ui-slider--indeterminate': indeterminate,
  });
  const wrapperClasses = classnames('ws-ui-slider-wrapper', {
    'ws-ui-slider-wrapper--with-value-label': isValueLabelDisplayed,
  });

  const Wrapper = isTooltipEnabled ? RCSliderWithTooltip : RCSlider;

  const restProps = {
    ...(value !== null && {
      value,
    }),
    ...(isTooltipEnabled && { tipFormatter: tooltipValueFormatter }),
  };

  const handleBlur = useCallback(() => {
    onBlur();
    setHandleFocus(false);
  }, [onBlur]);

  useEffect(() => {
    const handleKeyUp = (e) => {
      const isOnHandle = e.target.className.includes('rc-slider-handle');
      setHandleFocus(isOnHandle);
    };
    document.addEventListener('keyup', handleKeyUp);
    return () => document.removeEventListener('keyup', handleKeyUp);
  }, [isHandleFocus]);

  const HandleComponent = useCallback(
    (sliderHandleProps) => <Handle {...sliderHandleProps} {...handleProps} />,
    [handleProps]
  );

  return (
    <div className={wrapperClasses}>
      <Wrapper
        className={currentClasses}
        startPoint={startPoint}
        defaultValue={defaultValue}
        step={step}
        min={min}
        max={max}
        disabled={disabled}
        handle={HandleComponent}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={handleBlur}
        onBeforeChange={onBeforeChange}
        onAfterChange={onAfterChange}
        {...restProps}
      />
      {isValueLabelDisplayed && <Heading type="heading-6">{value || defaultValue}</Heading>}
    </div>
  );
};

Slider.propTypes = {
  startPoint: PropTypes.number,
  defaultValue: PropTypes.number,
  value: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  disabled: PropTypes.bool,
  isTooltipEnabled: PropTypes.bool,
  indeterminate: PropTypes.bool,
  valueLabelDisplay: PropTypes.oneOf(Object.values(VALUE_LABEL_DISPLAY_TYPES)),
  size: PropTypes.oneOf(Object.values(SIZES)),
  tooltipValueFormatter: PropTypes.func,
  handleProps: PropTypes.shape({}),
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onBeforeChange: PropTypes.func,
  onAfterChange: PropTypes.func,
};

export default Slider;
